/**
 * Kinematic character controller on Rapier, tuned for feel:
 *  - acceleration/friction velocity model (no instant velocity snapping)
 *  - gamey gravity (stronger than Earth) with variable jump height
 *  - coyote time + jump buffering so edge jumps and early taps never drop
 *  - dash burst with cooldown
 * All tunables live in TUNING at the top — this file IS the feel milestone.
 */
import type RAPIER from "@dimforge/rapier3d-compat";
import type { InputFrame } from "../core/Input.js";

export const TUNING = {
  capsuleRadius: 0.4,
  capsuleHalfHeight: 0.5, // total height = 2*(halfHeight + radius) = 1.8m

  maxSpeed: 8.5, // m/s on the ground
  groundAccel: 70, // m/s^2 toward wish velocity
  groundFriction: 90, // m/s^2 decel when no input
  airAccel: 30,
  airFriction: 4,

  gravity: -30, // gamey, snappy arcs
  jumpVelocity: 11.5, // apex ~2.2m
  jumpCutFactor: 0.45, // release Space while rising → cut vy
  coyoteTime: 0.12,
  jumpBuffer: 0.12,
  groundStick: -2, // small downward bias while grounded

  dashSpeed: 24,
  dashDuration: 0.19, // user feel-test: "a little longer"
  dashCooldown: 0.32, // user feel-test: "cooldown shorter"

  characterMass: 78, // shoves crates convincingly
} as const;

export interface PlayerState {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  grounded: boolean;
  dashing: boolean;
  /** facing yaw derived from horizontal velocity (for mesh orientation) */
  facingYaw: number;
}

export class CharacterController {
  private readonly body: RAPIER.RigidBody;
  private readonly collider: RAPIER.Collider;
  private readonly cc: RAPIER.KinematicCharacterController;

  private vx = 0;
  private vy = 0;
  private vz = 0;
  private grounded = false;
  private facingYaw = 0;

  private coyoteTimer = 0;
  private jumpBufferTimer = 0;
  private dashTimer = 0;
  private dashCooldownTimer = 0;
  private dashDirX = 0;
  private dashDirZ = 0;

  constructor(
    api: typeof RAPIER,
    world: RAPIER.World,
    private readonly spawn: { x: number; y: number; z: number },
  ) {
    const bodyDesc = api.RigidBodyDesc.kinematicPositionBased().setTranslation(
      spawn.x,
      spawn.y,
      spawn.z,
    );
    this.body = world.createRigidBody(bodyDesc);
    const colliderDesc = api.ColliderDesc.capsule(
      TUNING.capsuleHalfHeight,
      TUNING.capsuleRadius,
    );
    this.collider = world.createCollider(colliderDesc, this.body);

    this.cc = world.createCharacterController(0.05);
    this.cc.enableAutostep(0.45, 0.25, true);
    this.cc.enableSnapToGround(0.35);
    this.cc.setMaxSlopeClimbAngle((55 * Math.PI) / 180);
    this.cc.setMinSlopeSlideAngle((60 * Math.PI) / 180);
    this.cc.setApplyImpulsesToDynamicBodies(true);
    this.cc.setCharacterMass(TUNING.characterMass);
  }

  reset(): void {
    this.body.setNextKinematicTranslation(this.spawn);
    this.body.setTranslation(this.spawn, true);
    this.vx = this.vy = this.vz = 0;
    this.grounded = false;
    this.coyoteTimer = 0;
    this.jumpBufferTimer = 0;
    this.dashTimer = 0;
    this.dashCooldownTimer = 0;
  }

  /** Portal traversal: hard-set position + velocity (momentum carry). */
  teleport(pos: { x: number; y: number; z: number }, vel: { x: number; y: number; z: number }): void {
    this.body.setNextKinematicTranslation(pos);
    this.body.setTranslation(pos, true);
    this.vx = vel.x;
    this.vy = vel.y;
    this.vz = vel.z;
    this.grounded = false;
    this.coyoteTimer = 0;
    const hSpeed = Math.hypot(this.vx, this.vz);
    if (hSpeed > 0.5) this.facingYaw = Math.atan2(this.vx, this.vz);
  }

  /** One fixed physics step. Call BEFORE world.step(). */
  step(dt: number, input: InputFrame): void {
    const t = TUNING;

    // --- timers ---
    this.coyoteTimer = Math.max(0, this.coyoteTimer - dt);
    this.jumpBufferTimer = Math.max(0, this.jumpBufferTimer - dt);
    this.dashTimer = Math.max(0, this.dashTimer - dt);
    this.dashCooldownTimer = Math.max(0, this.dashCooldownTimer - dt);
    if (input.jumpPressed) this.jumpBufferTimer = t.jumpBuffer;

    // --- dash ---
    if (input.dashPressed && this.dashCooldownTimer === 0) {
      const len = Math.hypot(input.moveX, input.moveZ);
      // dash toward input, or facing if neutral
      this.dashDirX = len > 0.01 ? input.moveX / len : Math.sin(this.facingYaw);
      this.dashDirZ = len > 0.01 ? input.moveZ / len : Math.cos(this.facingYaw);
      this.dashTimer = t.dashDuration;
      this.dashCooldownTimer = t.dashCooldown;
    }
    const dashing = this.dashTimer > 0;

    // --- horizontal velocity model ---
    if (dashing) {
      this.vx = this.dashDirX * t.dashSpeed;
      this.vz = this.dashDirZ * t.dashSpeed;
    } else {
      const accel = this.grounded ? t.groundAccel : t.airAccel;
      const friction = this.grounded ? t.groundFriction : t.airFriction;
      const wishX = input.moveX * t.maxSpeed;
      const wishZ = input.moveZ * t.maxSpeed;
      const hasInput = Math.hypot(input.moveX, input.moveZ) > 0.01;
      const rate = hasInput ? accel : friction;
      this.vx = approach(this.vx, wishX, rate * dt);
      this.vz = approach(this.vz, wishZ, rate * dt);
    }

    // --- vertical ---
    if (dashing) {
      this.vy = 0; // dash is a flat burst
    } else {
      this.vy += t.gravity * dt;
      if (this.grounded && this.vy < 0) this.vy = t.groundStick;
      // variable jump height: cut upward velocity on early release
      if (!input.jumpHeld && this.vy > 0) this.vy *= 1 - (1 - t.jumpCutFactor) * (dt * 30);
    }

    // --- jump (buffered + coyote) ---
    const canJump = this.grounded || this.coyoteTimer > 0;
    if (this.jumpBufferTimer > 0 && canJump && !dashing) {
      this.vy = t.jumpVelocity;
      this.jumpBufferTimer = 0;
      this.coyoteTimer = 0;
      this.grounded = false;
    }

    // --- move through the world ---
    const desired = { x: this.vx * dt, y: this.vy * dt, z: this.vz * dt };
    this.cc.computeColliderMovement(this.collider, desired);
    const corrected = this.cc.computedMovement();
    const wasGrounded = this.grounded;
    this.grounded = this.cc.computedGrounded();
    if (wasGrounded && !this.grounded && this.vy <= 0) this.coyoteTimer = TUNING.coyoteTime;

    // head bonk: moving up but the solver ate the vertical motion
    if (this.vy > 0 && corrected.y < desired.y * 0.5) this.vy = 0;
    // landed: kill downward velocity
    if (this.grounded && this.vy < 0) this.vy = t.groundStick;

    const pos = this.body.translation();
    this.body.setNextKinematicTranslation({
      x: pos.x + corrected.x,
      y: pos.y + corrected.y,
      z: pos.z + corrected.z,
    });

    // --- facing (render hint only) ---
    const hSpeed = Math.hypot(this.vx, this.vz);
    if (hSpeed > 0.5) this.facingYaw = Math.atan2(this.vx, this.vz);
  }

  state(): PlayerState {
    const p = this.body.translation();
    return {
      x: p.x,
      y: p.y,
      z: p.z,
      vx: this.vx,
      vy: this.vy,
      vz: this.vz,
      grounded: this.grounded,
      dashing: this.dashTimer > 0,
      facingYaw: this.facingYaw,
    };
  }
}

/** Move `current` toward `target` by at most `maxDelta` (no overshoot). */
function approach(current: number, target: number, maxDelta: number): number {
  const delta = target - current;
  if (Math.abs(delta) <= maxDelta) return target;
  return current + Math.sign(delta) * maxDelta;
}
